import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FaceRecognitionSettingsPublicView, getFaceRecognitionPublicSettings } from '../api/Attendance/faceRecognitionSettings';
import { useSignalRConnection } from './useSignalRConnection';

/**
 * Hook that subscribes to SignalR for real-time face recognition settings updates.
 * When an Operator updates settings, all connected users will receive the update
 * in real-time without needing to refresh.
 *
 * @returns Object containing current settings, loading state, and manual refresh function
 */
export const useFaceRecognitionSettingsSignalR = () => {
  const { subscribe, subscribeToFaceRecognitionSettings, isConnected } = useSignalRConnection();
  const [settings, setSettings] = useState<FaceRecognitionSettingsPublicView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isLoggedIn = useSelector((state: any) => state.user?.isLoggedIn);

  // Fetch initial settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getFaceRecognitionPublicSettings();
      if (result.status === 200 && result.payload) {
        setSettings(result.payload);
      } else {
        setError('Failed to load face recognition settings');
      }
    } catch (err) {
      console.error('Failed to fetch face recognition settings:', err);
      setError('Failed to load face recognition settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handler for face recognition settings updates
  const handleSettingsUpdate = useCallback((updatedSettings: FaceRecognitionSettingsPublicView) => {
    console.log('FaceRecognitionSettingsUpdated received:', updatedSettings);
    setSettings(updatedSettings);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;

    // Fetch initial settings
    fetchSettings();

    // Subscribe to the face recognition settings group
    subscribeToFaceRecognitionSettings().catch(err => {
      console.error('Failed to subscribe to face recognition settings:', err);
    });

    // Subscribe to the FaceRecognitionSettingsUpdated event
    const unsubscribe = subscribe('FaceRecognitionSettingsUpdated', handleSettingsUpdate);

    return () => {
      unsubscribe();
    };
  }, [isLoggedIn, fetchSettings, subscribe, subscribeToFaceRecognitionSettings, handleSettingsUpdate]);

  return {
    settings,
    loading,
    error,
    refresh: fetchSettings,
    isConnected,
  };
};

export default useFaceRecognitionSettingsSignalR;
